import React, { useState, useEffect, useRef, useMemo } from 'react';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import ActivityLogs from './ActivityLogs';
import { IconButton, Pagination } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { timeAgo, convertSessionTime, formatDate } from '../../utils/dateTime';
import { fetchUsersList, toggleNcrPin } from '../../api/user';
import SuspendCard from './SuspendCard';
import ProfileCard from './ProfileCard';
import Loader from '../common/Loader/Loader';
import { FRONTEND_URL } from '../../config/config';
import { loginUser } from '../../api/auth';
import { useNotification } from '../../hooks/useNotification';
import UserTableSkeleton from './UserTableSkeleton';
import Tooltip from '@mui/material/Tooltip';
import UserProfileMainPage from '../user-profile-main/UserProfileMainPage';
import { setSearchTermAction } from '../../containers/app-container/reducer/filterSlice';
import { initColumns, toggleColumn } from '../../containers/app-container/reducer/visibleColumnsSlice';
import { CustomTooltipCell } from '../common/CustomTooltipCell';
import { statusLabel } from '../../utils/memberStatus';
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 600,
  width: '95%',
  margin: '0 auto',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: 24,
  p: 4,
  maxHeight: '80vh', // Adjust the height for scrolling
  overflow: 'auto', // Make content scrollable
};
export const style2 = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 1300,
  width: '95%',
  margin: '0 auto',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: 24,
  p: '0 30px 30px 30px',
  maxHeight: '80vh', // Adjust the height for scrolling
  overflow: 'auto', // Make content scrollable
};

const styleUser = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 1080,
  width: '95%',
  margin: '0 auto',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: 24,
  p: 4,
  maxHeight: '80vh', // Adjust the height for scrolling
  overflow: 'auto', // Make content scrollable
};

const visibleColumnKey = [
  "email",
  "age",
  "gender",
  "location",
  "userName",
  "firstName",
  "subscription",
  "registrationDate",
  "verification",
  "accountStatus",
  "hideReason",
  "ncrPinned",
  "flags",
  "blockedByUsers",
  "action"
]

// Colour per server statusBucket — the classes the old inline rule gave the same state.
const STATUS_CLASS = {
  approved: 'bg-success-700',
  hidden: 'bg-warning-500',
  inactive: 'bg-danger-500',
  ghost: 'bg-danger-400',
  deactivated: 'bg-warning-500',
  suspended: 'bg-danger-600',
  deleted: 'bg-neutral-600',
};

// Rollout fallback for a server that does not send statusBucket yet — remove once it does.
const legacyUserStatus = (user) => {
  if (user?.suspend) return { label: 'Suspended', className: 'bg-danger-600' };
  if (user?.isInvisible) return { label: 'Hidden', className: 'bg-warning-500' };
  if (user?.active === false) return { label: 'Deactivated', className: 'bg-warning-500' };
  return { label: 'Active', className: 'bg-success-700' };
};

// The server decides the status (getUsers `statusBucket`); this only names and colours it.
const getUserStatus = (user) => (user?.statusBucket
  ? { label: statusLabel(user.statusBucket), className: STATUS_CLASS[user.statusBucket] || 'bg-neutral-600' }
  : legacyUserStatus(user));

const UserList = () => {
    const searchTermFromStore = useSelector((state) => state?.filter?.searchTerm);
    const dispatch = useDispatch()
  const tableContainerRef = useRef(null);
  const scrollThumbRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });

  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState(null);
  const [open, setOpen] = useState(false);
  const [openSuspend, setOpenSuspend] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [userData, setUserData] = useState([]);
  const visibleColumns = useSelector(state => state.visibleColumns.pages.bot_management || {});


  // const { filter } = useSelector((state) => state);
  const [currentPage, setCurrentPage] = useState(1);
  const filter = useSelector((state) => state.filter);
  const filterWithoutUTM = useMemo(() => {
    const { utm_options, isBot, ...rest } = filter;
    setCurrentPage(1)  //reset to pg 1 when filter change
    return rest;
  }, [JSON.stringify(filter)]);
  const [selectedUser, setSelectedUser] = useState(false);
  // Which bot is mid-toggle, so the row can show progress and the action can be
  // blocked against double-clicks (mirrors demotingUserId in UserList).
  const [pinningUserId, setPinningUserId] = useState(null);
  const [pageLimit, setPageLimit] = useState(25);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tokenId, setTokenId] = useState(false)
  const { showErrorNotification, showSuccessNotification } = useNotification();


  // Scroll thumb width calculation
  const thumbWidth = useMemo(() => {
    if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
      const ratio = scrollState.clientWidth / scrollState.scrollWidth;
      return Math.max(ratio * scrollState.clientWidth, 50);
    }
    return 100;
  }, [scrollState]);

  // Scroll thumb position
  const thumbPosition = useMemo(() => {
    if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
      const ratio = scrollState.scrollLeft / (scrollState.scrollWidth - scrollState.clientWidth);
      return ratio * (scrollState.clientWidth - thumbWidth);
    }
    return 0;
  }, [scrollState, thumbWidth]);

  const updateScrollState = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setScrollState({
        scrollLeft,
        scrollWidth,
        clientWidth,
      });
    }
  };

  const handleTableScroll = () => {
    updateScrollState();
  };

  const handleStickyScroll = (e) => {
    if (!tableContainerRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickX = e.clientX - trackRect.left;
    const thumbCenter = thumbWidth / 2;

    const newScrollLeft = (clickX - thumbCenter) / (trackRect.width - thumbWidth) *
      (scrollState.scrollWidth - scrollState.clientWidth);

    tableContainerRef.current.scrollLeft = newScrollLeft;
  };

  const handleThumbDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startScrollLeft = scrollState.scrollLeft;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const trackWidth = scrollState.clientWidth - thumbWidth;
      const scrollableWidth = scrollState.scrollWidth - scrollState.clientWidth;

      if (trackWidth > 0) {
        const newScrollLeft = startScrollLeft + (deltaX / trackWidth) * scrollableWidth;
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollLeft = Math.max(0, Math.min(newScrollLeft, scrollableWidth));
        }
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);

    return () => {
      window.removeEventListener('resize', updateScrollState);
    };
  }, [userData, visibleColumns]);


  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await fetchUsersList({
        filter:{...filter,requestType:"bot_management"},
        setUserData,
        page: currentPage,
        limit: pageLimit,
        skip: 0,
        // isBot: true,
        searchTerm:filterWithoutUTM?.searchTerm,
        utm_payload: filter.utm_payload
      });
      setTotalUsers(data.totalUsersCount);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error('Error while loading user data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [JSON.stringify(filterWithoutUTM), openSuspend, currentPage]);

  const handleOpen = (user) => {
    setSelectedUser(user);
    setOpen(true);
  };

  const handleOpenSuspend = (user) => {
    setSelectedUser(user);
    setOpenSuspend(true);
  };

  const handleOpenProfile = (user) => {
    setSelectedUser(user);
    setOpenProfile(true);
  };

  // Row-level "click anywhere to view profile", same as the Channel-Attribution
  // members list. Clicks on interactive controls (Action menu, Activity Logs,
  // links/buttons/inputs, or anything tagged data-no-row-open) are ignored so
  // their own behaviour still works.
  const handleRowOpenProfile = (e, user) => {
    // MUI's Select opens on mousedown and portals its menu + full-screen
    // backdrop to <body>; the follow-up click then lands on that overlay and
    // bubbles here through the React tree with a target that isn't inside the
    // row — so anything outside the row's own DOM must never open the profile.
    if (!e.currentTarget.contains(e.target)) return;
    if (e.target.closest('a, button, input, select, [role="button"], .MuiFormControl-root, [data-no-row-open]')) return;
    handleOpenProfile(user);
  };

  // Pin / unpin a bot in the fixed Delhi-NCR search block. No confirmation
  // modal: the action is instantly reversible and carries no reason field,
  // unlike Hide from Search. The row is patched locally on success so the
  // column flips immediately without refetching the whole page.
  const handleToggleNcrPin = async (user) => {
    if (!user?._id || pinningUserId) return;
    setPinningUserId(user._id);
    try {
      const res = await toggleNcrPin({ userId: user._id });
      const nowPinned = res?.ncrPinned ?? !user.ncrPinned;
      setUserData((prev) =>
        (prev || []).map((u) =>
          u._id === user._id ? { ...u, ncrPinned: nowPinned, ncrPinOrder: res?.ncrPinOrder ?? u.ncrPinOrder } : u
        )
      );
      showSuccessNotification(
        res?.message ||
        (nowPinned ? 'Pinned in Delhi-NCR search' : 'Unpinned from Delhi-NCR search')
      );
    } catch (err) {
      showErrorNotification(
        err?.response?.data?.error || err?.message || 'Could not update the NCR pin'
      );
    } finally {
      setPinningUserId(null);
    }
  };

  const handleClose = () => setOpen(false);
  const handleCloseSuspend = () => setOpenSuspend(false);
  const handleCloseProfile = () => setOpenProfile(false);
  const handleColumnToggle = (key) => {
    dispatch(
    toggleColumn({
      pageKey: "bot_management",
      column: key
    }))
  };

    useEffect(() => {
    dispatch(
      initColumns({
        pageKey: "bot_management",
        columns: visibleColumnKey
      })
    );
  }, []);

  const filteredData = userData
  // .filter((user) =>
  //   Object.values(user).some(
  //     (value) =>
  //       typeof value === 'string' &&
  //       value.toLowerCase().includes(searchTerm.toLowerCase())
  //   )
  // );

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleUserEditView = async (user) => {
    try {
      const res = await loginUser(user?.email, 'bot1234')

      if (res?.data?.tokenId) {
        window.open(`${FRONTEND_URL}/authCheck?authCheckRef=${res?.data?.tokenId}`, '_blank');

      } else {
        showErrorNotification('Login failed, please check your credentials');
      }
    } catch (error) {
      showErrorNotification('Login failed', error?.message);
      console.error('Login failed:', error);
    }
  };

  const columnHeaders = [
    { key: 'email', label: 'Email' },
    { key: 'age', label: 'Age' },
    { key: 'gender', label: 'Gender' },
    { key: 'location', label: 'Location' },
    { key: 'userName', label: 'Username' },
    { key: 'firstName', label: 'First Name' },
    { key: 'subscription', label: 'Subscription Status' },
    { key: 'registrationDate', label: 'Registration Date' },
    // { key: 'session', label: 'Session' },
    // { key: 'interested', label: 'Interested' },
    // { key: 'profileView', label: 'Profile View' },
    // { key: 'conversation', label: 'Conversation' },
    // { key: 'duration', label: 'Duration' },
    // { key: 'appInstalled', label: 'App Installed' },
    { key: 'verification', label: 'Verification' },
    { key: 'accountStatus', label: 'Account Status' },
    { key: 'hideReason', label: 'Reason' },
    { key: 'ncrPinned', label: 'NCR Pin' },
    { key: 'flags', label: 'Flags' },
    { key: 'blockedByUsers', label: 'Blocked by Users' },
    // { key: 'activityLogs', label: 'Activity Logs' },
    { key: 'action', label: 'Action' },
  ];

  const handleSearch = () => {
    fetchData()
  }

  useEffect(() => {
    if (searchTerm === undefined || searchTerm === null) return

    clearTimeout(debounceTimeoutRef.current);

    debounceTimeoutRef.current = setTimeout(() => {
      dispatch(setSearchTermAction({ searchTerm }))
    }, 300);

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (searchTermFromStore == null && searchTerm) setSearchTerm(null);
  }, [searchTermFromStore])


  return (
    <>
      <div className="mb-16 d-flex justify-content-between align-items-center">
        <h6 className="text-lg mb-0">Bot User List</h6>
        <div className="d-flex align-items-center gap-2">
          <form className="navbar-search" onSubmit={e => e.preventDefault()}>
            <input
              type="text"
              name="search"
              placeholder="Search"
              value={searchTerm || ''}
              autoComplete="off"
              onChange={(e) =>{ setSearchTerm(e.target.value)}}
            />
            <span className="icon">
              <iconify-icon icon="ion:search-outline" />
            </span>
          </form>
          <div className="dropdown">
            <Tooltip title="Manage Columns" arrow placement="top">
              <button
                className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <ViewColumnOutlinedIcon />
              </button>
            </Tooltip>
            <ul className="dropdown-menu manage-list">
              {columnHeaders.map((column) => (
                <li
                  key={column.key}
                  className="dropdown-item d-flex align-items-center gap-2"
                >
                  <input
                    className="form-check-input"
                    style={{ width: '18px', height: '18px' }}
                    type="checkbox"
                    checked={visibleColumns[column.key]}
                    onChange={() => handleColumnToggle(column.key)}
                  />
                  {column.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="table-scroll-container">
        <div
          ref={tableContainerRef}
          className="custom-horizontal-scroll scroll-sm"
          onScroll={handleTableScroll}
           style={{ 
    maxHeight: 'calc(100vh - 250px)', // Adjust this value as needed
    overflow: 'auto',
    position: 'relative'
  }}
        >
          <table className="table bordered-table sm-table mb-0" sxle={{position: 'relative',}}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10, }}>
              <tr style={{ background: '#f4f4f4', textAlign: 'left' }}>
                <th>#</th>
                <th className="text-center">Profile</th>
                {visibleColumns.name && <th>Name</th>}
                {visibleColumns.email && <th className="text-center">Email</th>}
                {visibleColumns.age && <th className="text-center">Age</th>}
                {visibleColumns.gender && <th className="text-center">Gender</th>}
                {visibleColumns.location && (
                  <th className="text-center">Location</th>
                )}
                {visibleColumns.userName && (
                  <th className="text-center">Username</th>
                )}
                {visibleColumns.firstName && (
                  <th className="text-center">First Name</th>
                )}
                {visibleColumns.subscription && (
                  <th className="text-center">Subscription Status</th>
                )}
                {visibleColumns.registrationDate && (
                  <th className="text-center">Registration Date</th>
                )}
                {visibleColumns.session && (
                  <th className="text-center">Session</th>
                )}
                {visibleColumns.interested && (
                  <th className="text-center">Interested</th>
                )}
                {visibleColumns.profileView && (
                  <th className="text-center">Profile View</th>
                )}
                {visibleColumns.conversation && (
                  <th className="text-center">Conversation</th>
                )}
                {visibleColumns.duration && (
                  <th className="text-center">Duration</th>
                )}
                {visibleColumns.appInstalled && (
                  <th className="text-center">App Installed</th>
                )}
                {visibleColumns.verification && (
                  <th className="text-center">Verification</th>
                )}
                {visibleColumns.accountStatus && (
                  <th className="text-center">Account Status</th>
                )}
                {visibleColumns.hideReason && (
                  <th className="text-center">Reason</th>
                )}
                {visibleColumns.lastLogin && (
                  <th className="text-center">Last Login</th>
                )}
                {visibleColumns.ncrPinned && (
                  <th className="text-center">NCR Pin</th>
                )}
                {visibleColumns.flags && (
                  <th className="text-center">Flag/Report by Users</th>
                )}
                {visibleColumns.blockedByUsers && (
                  <th className="text-center">Blocked by Users</th>
                )}
                {visibleColumns.activityLogs && (
                  <th className="text-center">Activity Logs</th>
                )}
                {visibleColumns.action && <th className="text-center">Action</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <UserTableSkeleton
                  rows={10}
                  columns={Object.keys(visibleColumns).filter((key) => visibleColumns[key]).length + 2}
                />
              ) : filteredData.length > 0 ? (
                filteredData.map((user, index) => (
                  <tr
                    key={index}
                    onClick={(e) => handleRowOpenProfile(e, user)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    {/* <td>{index + 1}</td> */}
                    <td className="text-center">
                      <Tooltip title="View profile" arrow placement="top">
                        <img
                          src={user.profilePicture || '/assets/images/admin-panel/avatar-placeholder.svg'}
                          onError={(e) => { e.currentTarget.src = '/assets/images/admin-panel/avatar-placeholder.svg'; }}
                          alt="profile"
                          onClick={(e) => { e.stopPropagation(); handleOpenProfile(user); }}
                          className="w-40-px h-40-px rounded-circle flex-shrink-0 overflow-hidden"
                          style={{ cursor: 'pointer', objectFit: 'cover' }}
                        />
                      </Tooltip>
                    </td>
                    {visibleColumns.name && (
                      <td>
                        <div className="d-flex align-items-center">
                          <img
                            src={user.image}
                            alt=""
                            className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden"
                          />
                          <div className="flex-grow-1">
                            <h6 className="text-md mb-0 fw-medium">
                              {user.name}
                            </h6>
                          </div>
                        </div>
                      </td>
                    )}
                    {visibleColumns.email && (
                      <td className="text-center">{user.email}</td>
                    )}
                    {visibleColumns.age && (
                      <td className="text-center">{user.age}</td>
                    )}
                    {visibleColumns.gender && (
                      <td className="text-center">{user.gender}</td>
                    )}
                    {visibleColumns.location && (
                      <td className="text-center">
                        {[user.city, user.state, user.country].filter(Boolean).join(", ")}
                      </td>
                    )}
                    {visibleColumns.userName && (
                      <td className="text-center">{ user.userName}</td>
                    )}
                    {visibleColumns.firstName && (
                      <td className="text-center">{user.firstName}</td>
                    )}
                    {/* Subscription */}
                    {visibleColumns.subscription && (
                      <td className="text-center">
                        {!user.isSubscribed && (
                          <div className="freesub">Free</div>
                        )}
                        {user.subscriptionPlan === 'Gold' && (
                          <div className="goldsub">
                            <img
                              src="/assets/images/admin-panel/gold.svg"
                              className="img-fluid"
                              alt="Gold"
                            />
                            Gold
                          </div>
                        )}
                        {user.subscriptionPlan === 'Diamond' && (
                          <div className="Diamond">
                            <img
                              src="/assets/images/admin-panel/dimond.svg"
                              className="img-fluid"
                              alt="Diamond"
                            />
                            Diamond
                          </div>
                        )}
                      </td>
                    )}
                    {visibleColumns.registrationDate && (
                      <td className="text-center">
                        {formatDate(user.createdAt)}
                      </td>
                    )}
                    {visibleColumns.session && (
                      <td className="text-center">{user.sessionCount}</td>
                    )}
                    {/* Interested with Favorite Icon */}
                    {visibleColumns.interested && (
                      <td className="text-center">
                        <div className="interested d-flex justify-content-center gap-10">
                          {user.showFavorite && (
                            <FavoriteIcon className="text-danger-500" />
                          )}
                          {user.interestedCount}
                        </div>
                      </td>
                    )}
                    {visibleColumns.profileView && (
                      <td className="text-center">{user.viewCount}</td>
                    )}
                    {visibleColumns.conversation && (
                      <td className="text-center">{user.conversationCount}</td>
                    )}
                    {visibleColumns.duration && (
                      <td className="text-center">
                        {convertSessionTime(user.sessionCount)}
                      </td>
                    )}
                    {/* App Installed */}
                    {visibleColumns.appInstalled && (
                      <td className="text-center">
                        {user.appInstalled ? (
                          <span className="badge text-sm fw-medium text-warning-900 bg-warning-50 px-20 py-9 rounded-pill text-white yes">
                            YES
                          </span>
                        ) : (
                          <span className="badge text-sm fw-medium text-neutral-500 bg-neutral-200 px-20 py-9 rounded-pill text-white no">
                            NO
                          </span>
                        )}
                      </td>
                    )}
                    {visibleColumns.verification && (
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-10 verification">
                          {user.isProfileVerified && (
                            <img
                              src="/assets/images/admin-panel/verified.svg"
                              className="img-fluid"
                              alt="verification"
                            />
                          )}
                          {user.isProfileVerified ? 'Verified' : 'Pending'}
                        </div>
                      </td>
                    )}
                    {/* Account Status */}
                    {visibleColumns.accountStatus && (
                          <td
                        className={`accountStatus text-center text-white ${getUserStatus(user).className}`}
                      >
                        {getUserStatus(user).label}
                      </td>
                    )}
                    {visibleColumns.hideReason && (
                      <td className="text-center">
                        <CustomTooltipCell text={user?.suspend ? user?.suspendReason : user?.hideReason} limit={20} />
                      </td>
                    )}
                    {visibleColumns.lastLogin && (
                      <td className="text-center">
                        <div className="d-flex align-items-center gap-10 lastLogin justify-content-end">
                          {user.isOnline && (
                            <FiberManualRecordIcon
                              className="text-success-700"
                              sx={{ width: '16px', height: '16px' }}
                            />
                          )}
                          {user.isOnline
                            ? 'Online'
                            : user.lastLogin
                              ? timeAgo(user.lastLogin)
                              : '0'}
                          <img
                            src="/assets/images/admin-panel/desktop.svg"
                            className="img-fluid"
                            alt="activityLogs"
                          />
                          <img
                            src="/assets/images/admin-panel/mobile.svg"
                            className="img-fluid"
                            alt="activityLogs"
                          />
                        </div>
                      </td>
                    )}
                    {visibleColumns.ncrPinned && (
                      <td className="text-center">
                        {user.ncrPinned ? (
                          <span className="bg-success-focus text-success-main px-8 py-2 rounded-pill fw-medium text-sm">
                            Pinned #{(user.ncrPinOrder ?? 0) + 1}
                          </span>
                        ) : (
                          <span className="text-secondary-light text-sm">—</span>
                        )}
                      </td>
                    )}
                    {visibleColumns.flags && (
                      <td className="text-center">
                        <span className="border d-flex text-center justify-content-between radius-4 px-10">
                          {' '}
                          <img
                            src="/assets/images/admin-panel/flag.svg"
                            className="img-fluid"
                            alt="activityLogs"
                          />{' '}
                          {user.reportCount}
                        </span>
                      </td>
                    )}
                    {visibleColumns.blockedByUsers && (
                      <td className="text-center">
                        <span className="border d-flex text-center justify-content-center radius-4 text-decoration-underline">
                          {user.blockCount}
                        </span>
                      </td>
                    )}
                    {visibleColumns.activityLogs && (
                      <td
                        className="text-center"
                        onClick={() => handleOpen(user)}
                        style={{ cursor: 'pointer' }}
                      >
                        <img
                          src="/assets/images/admin-panel/activity.svg"
                          className="img-fluid"
                          alt="activityLogs"
                        />
                      </td>
                    )}
                    {visibleColumns.action && (
                      <td className="text-center">
                        <div className="dropdown">
                          <button
                            className="btn btn-outline-primary-600 not-active px-18 py-6 dropdown-toggle toggle-icon text-sm"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            Action
                          </button>
                          <ul className="dropdown-menu" style={{}}>
                            {
                              user.suspend ?
                                <li>
                                  <a
                                    className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                                    href="javascript:void(0)"
                                    onClick={() => handleOpenSuspend(user)}
                                  >
                                    Lift Suspension
                                  </a>
                                </li> :
                                <li>
                                  <a
                                    className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                                    href="javascript:void(0)"
                                    onClick={() => handleOpenSuspend(user)}
                                  >
                                    Suspend Bot
                                  </a>
                                </li>
                            }
                            {user.gender === 'Female' && (
                              <li>
                                <a
                                  className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                                  href="javascript:void(0)"
                                  onClick={() => handleToggleNcrPin(user)}
                                >
                                  {pinningUserId === user._id
                                    ? 'Updating…'
                                    : user.ncrPinned
                                      ? 'Unpin from NCR Search'
                                      : 'Pin in NCR Search'}
                                </a>
                              </li>
                            )}
                            <li>
                              <a
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                                href="javascript:void(0)"
                                onClick={() => handleOpenProfile(user)}
                              >
                                View
                              </a>
                            </li>
                            {/* <li>
                              <a href="javascript:void(0)"
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => handleUserEditView(user)}
                              >
                                Update
                              </a>
                            </li> */}
                            {/* <li>
                          <a
                            className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                            href="javascript:void(0)"
                            onClick={handleOpenSecond}
                          >
                            View
                          </a>
                        </li> */}
                          </ul>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="20" className="text-center py-3">
                    No data found
                  </td>
                </tr>
              )}

              {/* {userData.map((user, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                {/* Name with image

              </tr>
            ))} */}
            </tbody>
          </table>
        </div>
        {/* Sticky Scroll Bar */}
        {scrollState.scrollWidth > scrollState.clientWidth && (
          <div className="sticky-scroll-container">
            <div
              className="sticky-scroll-track"
              onClick={handleStickyScroll}
            >
              <div
                ref={scrollThumbRef}
                className="sticky-scroll-thumb"
                style={{
                  width: `${thumbWidth}px`,
                  transform: `translateX(${thumbPosition}px)`
                }}
                onMouseDown={handleThumbDrag}
              />
            </div>
          </div>
        )}
      </div>
      {/* Pagination */}
      {!loading && Boolean(totalPages) && <div className="d-flex justify-content-center mt-16">
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          // color="primary"
            sx={{
    "& .MuiPaginationItem-root": {
      color: "#000", // normal text color
      border: "1px solid #ddd",
    },

    "& .Mui-selected": {
      backgroundColor: "#ff4a4a !important", // active bg color
      color: "#fff",
    },

    "& .MuiPaginationItem-root:hover": {
      backgroundColor: "#ffe5e5",
    },
  }}
        />
      </div>}

      {/* <div className="pagination">
        <Button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </Button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <Button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </Button>
      </div> */}

            <Modal
              open={openSuspend}
              onClose={handleCloseSuspend}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <Box sx={style}>
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                  }}
                  onClick={handleCloseSuspend}
                >
                  <CloseIcon />
                </IconButton>
                <SuspendCard
                  userId={selectedUser?._id}
                  suspend={selectedUser?.suspend}
                  isBot={true}
                  handleClose={handleCloseSuspend}
                  fetchData={fetchData}
                />
              </Box>
            </Modal>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={styleUser}>
          <IconButton
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
            }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <ActivityLogs userId={selectedUser?._id} data={selectedUser} />
        </Box>
      </Modal>

      <Modal
        open={openProfile}
        onClose={handleCloseProfile}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style2}>
          <Box
            sx={{
              position: 'sticky',
              top: '0px',
              backgroundColor: 'white',
              zIndex: 10,
              padding: '10px',
              borderBottom: 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <IconButton
              sx={{
                position: 'absolute',
                top: 10,
                right: 0,
              }}
              onClick={handleCloseProfile}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {/* <ProfileCard userId={selectedUser?._id} /> */}
          <UserProfileMainPage userId={selectedUser?._id} />
        </Box>
      </Modal>
    </>
  );
};

export default UserList;
